import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl: string = import.meta.env.VITE_SUPABASE_URL ?? "";
const supabaseAnonKey: string = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

export interface SupabaseConfigStatus {
  isConfigured: boolean;
  missing: string[];
}

export const supabaseConfig: SupabaseConfigStatus = {
  isConfigured: supabaseUrl.length > 0 && supabaseAnonKey.length > 0,
  missing: [
    supabaseUrl.length === 0 ? "VITE_SUPABASE_URL" : null,
    supabaseAnonKey.length === 0 ? "VITE_SUPABASE_ANON_KEY" : null,
  ].filter((value): value is string => value !== null),
};

if (!supabaseConfig.isConfigured) {
  // eslint-disable-next-line no-console
  console.error(
    `Supabase env missing: ${supabaseConfig.missing.join(", ")}. ` +
      "Copy .env.example to .env and fill in the values, then restart `npm run dev`.",
  );
}

/**
 * `createClient` validates the URL with `new URL(...)` and throws on empty
 * strings, which would crash module evaluation and produce a blank page. We
 * only construct the real client when env is wired up; otherwise we expose a
 * lazy proxy that throws a descriptive error if anyone calls it.
 */
function buildClient(): SupabaseClient {
  if (supabaseConfig.isConfigured) {
    return createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: "pkce",
        storageKey: "diveroid-account-web-auth",
      },
    });
  }
  return new Proxy({} as SupabaseClient, {
    get(): never {
      throw new Error(
        `Supabase client unavailable. Missing env: ${supabaseConfig.missing.join(", ")}.`,
      );
    },
  });
}

export const supabase: SupabaseClient = buildClient();
